/**
 * TGC inline image zoom.
 *
 * Click/tap a product image to zoom 2.5x in place within its frame.
 * Desktop: cursor position drives pan automatically.
 * Mobile:  drag to pan, pinch is delegated to the browser via touch-action.
 * Reset:   click image again, click outside, ESC, or the corner ✕ button.
 */

const SCALE = 2.5;
const TRANSITION_MS = 250;
const INSTANCE_KEY = '__tgcInlineZoom';

const closeIconSVG =
  '<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">' +
  '<path stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M3 3l10 10M13 3L3 13"/>' +
  '</svg>';

class TgcImageZoom {
  constructor(gallery) {
    this.gallery = gallery;
    /** @type {HTMLElement|null} */ this.activeContainer = null;
    /** @type {HTMLImageElement|null} */ this.activeImage = null;
    /** @type {HTMLButtonElement|null} */ this.closeBtn = null;
    /** @type {{x:number,y:number}|null} */ this.touchOrigin = null;
    /** @type {{x:number,y:number}} */ this.touchOffset = { x: 0, y: 0 };
    this.touchMoved = false;

    this.bind();
  }

  bind() {
    for (const c of this.imageContainers()) c.classList.add('tgc-zoomable');

    this.gallery.addEventListener('click', this.onClick);
    this.gallery.addEventListener('pointermove', this.onPointerMove);
    this.gallery.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.gallery.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.gallery.addEventListener('touchend', this.onTouchEnd, { passive: true });
  }

  imageContainers() {
    return this.gallery.querySelectorAll('.product-media-container--image');
  }

  onClick = (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.closest('.tgc-zoom-close')) return; // its own handler resets

    const container = target.closest('.product-media-container--image');
    if (!container || !this.gallery.contains(container)) {
      if (this.activeContainer) this.reset();
      return;
    }

    const img = container.querySelector('img.product-media__image');
    if (!img) return;

    if (this.activeContainer === container) {
      this.reset();
    } else {
      if (this.activeContainer) this.reset();
      this.zoomIn(container, img, e);
    }
  };

  zoomIn(container, img, evt) {
    this.activeContainer = container;
    this.activeImage = img;
    this.touchOffset = { x: 0, y: 0 };
    container.classList.add('tgc-zoom-active');
    container.appendChild(this.ensureCloseBtn());

    const rect = container.getBoundingClientRect();
    const x = evt.clientX != null ? evt.clientX - rect.left : rect.width / 2;
    const y = evt.clientY != null ? evt.clientY - rect.top : rect.height / 2;

    img.style.transformOrigin = '0 0';
    img.style.transition = `transform ${TRANSITION_MS}ms ease`;
    this.applyCursorZoom(rect, x, y);

    // Bind document-level listeners while active
    this._docPointerDown = (e) => {
      if (!this.activeContainer) return;
      if (this.activeContainer.contains(/** @type {Node} */ (e.target))) return;
      this.reset();
    };
    this._docKeydown = (e) => {
      if (e.key === 'Escape' && this.activeContainer) this.reset();
    };
    document.addEventListener('pointerdown', this._docPointerDown, true);
    document.addEventListener('keydown', this._docKeydown);
  }

  /** translateX = cursorX * (1 - SCALE); same for y. */
  applyCursorZoom(rect, x, y) {
    if (!this.activeImage) return;
    const tx = clamp(x * (1 - SCALE), rect.width * (1 - SCALE), 0);
    const ty = clamp(y * (1 - SCALE), rect.height * (1 - SCALE), 0);
    this.activeImage.style.transform = `translate(${tx}px, ${ty}px) scale(${SCALE})`;
  }

  /** For touch drag: translate by accumulated offset, clamped into the same bounds. */
  applyTouchPan() {
    if (!this.activeImage || !this.activeContainer) return;
    const rect = this.activeContainer.getBoundingClientRect();
    this.touchOffset.x = clamp(this.touchOffset.x, rect.width * (1 - SCALE), 0);
    this.touchOffset.y = clamp(this.touchOffset.y, rect.height * (1 - SCALE), 0);
    this.activeImage.style.transform =
      `translate(${this.touchOffset.x}px, ${this.touchOffset.y}px) scale(${SCALE})`;
  }

  onPointerMove = (e) => {
    if (!this.activeContainer) return;
    if (e.pointerType === 'touch') return; // touch handled separately
    const rect = this.activeContainer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    if (this.activeImage) this.activeImage.style.transition = 'none';
    this.applyCursorZoom(rect, x, y);
  };

  onTouchStart = (e) => {
    if (!this.activeContainer || e.touches.length !== 1) return;
    if (!this.activeContainer.contains(/** @type {Node} */ (e.target))) return;
    const t = e.touches[0];
    this.touchOrigin = { x: t.clientX - this.touchOffset.x, y: t.clientY - this.touchOffset.y };
    this.touchMoved = false;
  };

  onTouchMove = (e) => {
    if (!this.activeContainer || !this.touchOrigin || e.touches.length !== 1) return;
    e.preventDefault();
    const t = e.touches[0];
    this.touchOffset.x = t.clientX - this.touchOrigin.x;
    this.touchOffset.y = t.clientY - this.touchOrigin.y;
    this.touchMoved = true;
    if (this.activeImage) this.activeImage.style.transition = 'none';
    this.applyTouchPan();
  };

  onTouchEnd = () => {
    this.touchOrigin = null;
    // touchMoved=false → it was a tap; the synthesized click event will toggle reset.
  };

  ensureCloseBtn() {
    if (this.closeBtn) return this.closeBtn;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tgc-zoom-close';
    btn.setAttribute('aria-label', 'Close zoom');
    btn.innerHTML = closeIconSVG;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.reset();
    });
    this.closeBtn = btn;
    return btn;
  }

  reset() {
    if (this.activeImage) {
      this.activeImage.style.transition = `transform ${TRANSITION_MS}ms ease`;
      this.activeImage.style.transform = '';
    }
    if (this.activeContainer) this.activeContainer.classList.remove('tgc-zoom-active');
    if (this.closeBtn && this.closeBtn.parentElement) this.closeBtn.remove();
    if (this._docPointerDown) {
      document.removeEventListener('pointerdown', this._docPointerDown, true);
      this._docPointerDown = null;
    }
    if (this._docKeydown) {
      document.removeEventListener('keydown', this._docKeydown);
      this._docKeydown = null;
    }
    this.activeImage = null;
    this.activeContainer = null;
    this.touchOrigin = null;
    this.touchOffset = { x: 0, y: 0 };
    this.touchMoved = false;
  }
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function init(root = document) {
  const galleries = root.querySelectorAll('media-gallery[data-tgc-inline-zoom]');
  galleries.forEach((g) => {
    if (g[INSTANCE_KEY]) return;
    g[INSTANCE_KEY] = new TgcImageZoom(g);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

// Theme editor: sections reload without a full page refresh.
document.addEventListener('shopify:section:load', (e) => {
  const target = /** @type {Element|null} */ (e.target);
  if (target) init(target);
});
